/**
 * Popover Custom Element
 * A reusable popover component with trigger and content
 *
 * Emits:
 * - 'popover:open' - when popover opens
 * - 'popover:close' - when popover closes
 * - 'popover:change' - when a value is selected (with detail.value)
 */

class PopoverElement extends HTMLElement {
  constructor() {
    super();

    this._isOpen = false;

    // Save bound references for proper cleanup
    this._boundOutsideClick = this._handleOutsideClick.bind(this);
    this._boundKeydown = this._handleKeydown.bind(this);
    this._boundTriggerClick = this._handleTriggerClick.bind(this);
    this._boundContentClick = this._handleContentClick.bind(this);
    this._boundOptionClick = this._handleOptionClick.bind(this);
  }

  connectedCallback() {
    // Cache DOM elements
    this.trigger = this.querySelector("[data-popover-trigger]");
    this.content = this.querySelector("[data-popover-content]");

    if (!this.trigger || !this.content) {
      console.warn("Popover: Missing trigger or content element");
      return;
    }

    this._bindEvents();
  }

  disconnectedCallback() {
    this._removeGlobalListeners();
    this._unbindEvents();
  }

  /* ===========================================
   * STATE
   * =========================================== */
  get isOpen() {
    return this._isOpen;
  }

  /* ===========================================
   * PUBLIC METHODS
   * =========================================== */
  open() {
    if (this._isOpen) return;

    this._isOpen = true;
    this.content.dataset.state = "open";
    this.trigger.dataset.state = "open";
    this.trigger.setAttribute("aria-expanded", "true");

    this._addGlobalListeners();
    this._emitEvent("popover:open");
  }

  close() {
    if (!this._isOpen) return;

    this._isOpen = false;
    this.content.dataset.state = "closed";
    this.trigger.dataset.state = "closed";
    this.trigger.setAttribute("aria-expanded", "false");

    this._removeGlobalListeners();
    this._emitEvent("popover:close");
  }

  toggle() {
    if (this._isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /* ===========================================
   * EVENT EMISSION
   * =========================================== */
  _emitEvent(eventName, detail = {}) {
    this.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        detail,
      }),
    );
  }

  /* ===========================================
   * EVENT BINDING
   * =========================================== */

  _handleTriggerClick(e) {
    e.stopPropagation();
    this.toggle();
  }

  _handleContentClick(e) {
    e.stopPropagation();
  }

  _handleOptionClick(e) {
    e.stopPropagation();
    const value = e.currentTarget.dataset.popoverValue;
    this._emitEvent("popover:change", { value });
    this.close();
  }

  _bindEvents() {
    this.trigger.addEventListener("click", this._boundTriggerClick);
    this.content.addEventListener("click", this._boundContentClick);

    // Value option clicks
    const options = this.content.querySelectorAll("[data-popover-value]");
    options.forEach((option) => {
      option.addEventListener("click", this._boundOptionClick);
    });
  }

  _unbindEvents() {
    this.trigger?.removeEventListener("click", this._boundTriggerClick);
    this.content?.removeEventListener("click", this._boundContentClick);

    const options = this.content?.querySelectorAll("[data-popover-value]");
    options?.forEach((option) => {
      option.removeEventListener("click", this._boundOptionClick);
    });
  }

  _addGlobalListeners() {
    document.addEventListener("click", this._boundOutsideClick);
    document.addEventListener("keydown", this._boundKeydown);
  }

  _removeGlobalListeners() {
    document.removeEventListener("click", this._boundOutsideClick);
    document.removeEventListener("keydown", this._boundKeydown);
  }

  _handleOutsideClick(e) {
    if (!this.contains(e.target)) {
      this.close();
    }
  }

  _handleKeydown(e) {
    if (e.key === "Escape") {
      this.close();
      this.trigger?.focus();
    }
  }
}

customElements.define("popover-element", PopoverElement);
